import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Space, Typography } from 'antd';

type Props = {
  value: string | null;
  label: string;
  fallback?: string;
  disabled?: boolean;
  maxLength?: number;
  onSave: (name: string) => Promise<void>;
};

/** Draft text survives a failed save; only a successful save replaces it. */
const DisplayNameEditor: React.FC<Props> = ({
  value,
  label,
  fallback,
  disabled,
  maxLength,
  onSave,
}) => {
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await onSave(draft);
      if (mounted.current) {
        setDraft(draft.trim());
        setSaved(true);
      }
    } catch (reason) {
      if (mounted.current) {
        setError(
          reason instanceof Error
            ? reason.message
            : 'Save failed. Please try again.',
        );
      }
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <Space orientation="vertical" style={{ width: '100%' }}>
      <Input.TextArea
        aria-label={label}
        value={draft}
        placeholder={fallback}
        autoSize={{ minRows: 2, maxRows: 5 }}
        maxLength={maxLength}
        disabled={disabled || saving}
        onChange={event => {
          setDraft(event.target.value);
          setSaved(false);
        }}
      />
      {fallback && (
        <Typography.Text type="secondary">
          Displayed: {draft.trim() || fallback}
        </Typography.Text>
      )}
      <Space>
        <Button
          type="primary"
          size="small"
          aria-label={`Save ${label}`}
          loading={saving}
          disabled={
            disabled ||
            saving ||
            draft.trim() === (value || '').trim() ||
            (!fallback && !draft.trim())
          }
          onClick={save}
        >
          Save
        </Button>
        {saved && <Typography.Text role="status">Saved</Typography.Text>}
      </Space>
      {error && <Alert type="error" showIcon title={error} />}
    </Space>
  );
};

export default DisplayNameEditor;
